import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getPaymentStatus } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    const sid = new URLSearchParams(location.search).get("session_id");
    if (!sid) { setState("error"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await getPaymentStatus(sid);
        if (res.payment_status === "paid") { await refreshUser(); setState("paid"); return; }
        if (attempts >= MAX_POLLS) { setState("pending"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= MAX_POLLS) { setState("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search, refreshUser]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center rounded-xl border border-zinc-800 bg-zinc-900 p-8">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-blue-400 animate-spin mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold text-white mt-4">Confirming your payment…</h1>
            <p className="text-zinc-400 text-sm mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold text-white mt-4">You're on Pro 🎉</h1>
            <p className="text-zinc-400 text-sm mt-2">Your workspace now has unlimited locations and team seats.</p>
            <button onClick={() => navigate("/")} data-testid="success-continue-btn" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white">
              Go to dashboard <ArrowRight className="h-4 w-4" />
            </button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="h-10 w-10 text-amber-400 mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold text-white mt-4">Payment processing</h1>
            <p className="text-zinc-400 text-sm mt-2">Your payment is still being confirmed. It'll update automatically — check the Billing page shortly.</p>
            <button onClick={() => navigate("/billing")} className="mt-6 text-sm font-semibold px-5 py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white">Back to billing</button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="h-12 w-12 text-red-400 mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold text-white mt-4">Something went wrong</h1>
            <p className="text-zinc-400 text-sm mt-2">We couldn't confirm this payment. Please return to billing and try again.</p>
            <button onClick={() => navigate("/billing")} className="mt-6 text-sm font-semibold px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white">Back to billing</button>
          </>
        )}
      </div>
    </div>
  );
}
